package team.platforma.multiplatform_material_extension.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.focusable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextField
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import team.platforma.multiplatform_material_extension.model.RequiredField
import team.platforma.multiplatform_material_extension.util.FormValidator
import team.platforma.multiplatform_material_extension.util.rememberStringValidator


@Composable
fun OutlineSmsCodeInputField(
    value: String,
    onValueChange: (String) -> Unit,
    items: Int = 4,
    itemSize: Dp = 56.dp,
    itemSpase: Dp = 8.dp,
    formValidator: FormValidator? = null,
    formKey: String? = null,
    requiredField: RequiredField = RequiredField(true, ""),
    validate: (Boolean) -> Unit = {},
    validateRegex: Regex = Regex("^[\\d]*$"),
    keyboardOptions: KeyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
    autoRequestFocus: Boolean = true
) {

    val focusRequester = remember { FocusRequester() }

    var rowWidth by remember { mutableStateOf(0) }

    var password by remember { mutableStateOf(TextFieldValue(value)) }

    var cursorPosition by remember { mutableStateOf(0) }
    var userInput by remember { mutableStateOf(false) }

    var focus by remember { mutableStateOf(false) }

    val fieldValidator = rememberStringValidator()

    var validatePasswordResult by remember { mutableStateOf(false) }

    var filled by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        if (autoRequestFocus) {
            focusRequester.requestFocus()
        }

        validatePasswordResult = fieldValidator.createValidateStringResult(
            required = requiredField.boolean,
            inputString = password.text,
            regex = validateRegex
        )

        val resultValidation = fieldValidator.createResultValidation(
            requiredField.boolean,
            validatePasswordResult,
            password.text,
            items
        )
        validate(resultValidation)

        formKey?.let { key ->
            formValidator?.let {
                it.setValidateState(key, resultValidation)
                it.initFormString(key, password.text)
                it.changeFormString(key, password.text)
            }
        }

        onValueChange(password.text)
    }

    Box {

        Row(
            horizontalArrangement = Arrangement.spacedBy(itemSpase),
            modifier = Modifier
                .onGloballyPositioned { coordinates ->
                    rowWidth = coordinates.size.width
                }
        ) {
            repeat(items) { index ->
                Box(
                    modifier = Modifier
                        .width(itemSize)
                        .height(itemSize)
                        .focusable(false)
                        .border(
                            BorderStroke(
                                width = setBorderStroke(cursorPosition, index, focus),
                                color = setIndicatorColor(cursorPosition, index, focus, filled)
                            ),
                            shape = OutlinedTextFieldDefaults.shape
                        ),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        modifier = Modifier.padding(0.dp),
                        text = password.text.getOrNull(index)?.toString() ?: "",
                        fontSize = (itemSize.value * 0.5).sp
                    )
                }
            }
        }

        TextField(
            value = password,
            onValueChange = {
                userInput = true

                if (it.text.length <= items && it.text.matches(validateRegex)) {
                    password = it
                    val selection = it.selection.start
                    cursorPosition = if (selection < 1) selection else selection - 1
                }


                validatePasswordResult = fieldValidator.createValidateStringResult(
                    required = requiredField.boolean,
                    inputString = password.text,
                    regex = validateRegex
                )

                val resultValidation = fieldValidator.createResultValidation(
                    requiredField.boolean,
                    validatePasswordResult,
                    password.text,
                    items
                )
                validate(resultValidation)

                formKey?.let { key ->
                    formValidator?.let { validator ->
                        validator.setValidateState(key, resultValidation)
                        validator.changeFormString(key, password.text)
                    }
                }

                filled = password.text.length == items

                onValueChange(password.text)
            },
            modifier = Modifier
                .width(with(LocalDensity.current) { rowWidth.toDp() })
                .height(itemSize)
                .focusRequester(focusRequester)
                .onFocusChanged {
                    focus = it.isFocused
                },
            colors = OutlinedTextFieldDefaults.colors().copy(
                cursorColor = Color.Transparent,
                focusedIndicatorColor = Color.Transparent,
                unfocusedIndicatorColor = Color.Transparent,
                disabledIndicatorColor = Color.Transparent,
                focusedTextColor = Color.Transparent,
                disabledTextColor = Color.Transparent,
                unfocusedTextColor = Color.Transparent
            ),
            textStyle = TextStyle(
                textAlign = TextAlign.Start,
                letterSpacing = itemSize.value.sp
            ),
            singleLine = true,
            keyboardOptions =  keyboardOptions
        )

        Row(
            horizontalArrangement = Arrangement.spacedBy(itemSpase),
        ) {
            repeat(items) { index ->
                Box(
                    modifier = Modifier
                        .width(itemSize)
                        .height(itemSize)
                        .clip(OutlinedTextFieldDefaults.shape)
                        .clickable {

                            password = TextFieldValue(
                                text = password.text,
                                selection = TextRange(minOf(index + 1, password.text.length))
                            )
                            cursorPosition = minOf(index, password.text.length)
                            focusRequester.requestFocus()
                        },

                )
            }
        }
    }

}

@Composable
private fun setIndicatorColor(cursorPosition: Int, index: Int, focus: Boolean, filled: Boolean): Color {
    if (filled) {
        return OutlinedTextFieldDefaults.colors().focusedIndicatorColor
    } else {
        if (cursorPosition == index && focus) {
            return OutlinedTextFieldDefaults.colors().focusedIndicatorColor
        } else {
            return OutlinedTextFieldDefaults.colors().unfocusedIndicatorColor
        }
    }

}

private fun setBorderStroke(cursorPosition: Int, index: Int, focus: Boolean): Dp {
    if (cursorPosition == index && focus) {
        return 2.dp
    } else {
        return 1.dp
    }
}

@Composable
private fun setTextColor(cursorPosition: Int, index: Int, focus: Boolean): Color {
    if (cursorPosition == index && focus) {
        return OutlinedTextFieldDefaults.colors().focusedTextColor
    } else {
        return OutlinedTextFieldDefaults.colors().disabledTextColor
    }
}

